







































































































































import { Component, Vue, Watch, Emit } from 'vue-property-decorator';
import Moveable from 'vue-moveable';
import { OnDragStart, OnDrag, OnScaleStart, OnScale } from 'moveable';
import { Action, Getter } from 'vuex-class';
import { GuestUser, RootGettersReturns } from '@/store/types';
import { ActiveItem, InterfaceActionsKeys, InterfaceActionsPayload } from '../../store/types/interface';
import ElementHolder from './Elements/ElementHolder.vue';
import Drawing from './Drawing.vue';
import Eraser from './Eraser.vue';
import { SessionActionsKeys, SessionActionsPayload, SessionStatus } from '../../store/types/session';
import { Stage, StageActionsKeys, StageActionsPayload, Focus } from '../../store/types/stageState';
import { ElementActionsKeys, ElementActionsPayload } from '../../store/types/element';
import { v4 as uuid } from 'uuid';
import { getFileType } from '@/plugins/fileType';
import Interface from './Interface.vue';
import { onDrop } from '@/utils/dropFile';
import { getIdFromUrl } from 'vue-youtube';
import ResizeObserver from 'resize-observer-polyfill';
import { isURL } from '@/utils';

import Logo from '../../assets/logo.svg?inline';
import UploadBoxElement from '@/components/Canvas/Elements/UploadBoxElement.vue';

@Component({
  components: {
    Interface,
    Moveable,
    Drawing,
    Eraser,
    ElementHolder,
    UploadBoxElement,
    Logo,
  },
})
export default class Canvas extends Vue {
  @Action(InterfaceActionsKeys.CHANGE_ACTIVE_ITEM) toolbarChangeActiveItem!: (
    payload: InterfaceActionsPayload['CHANGE_ACTIVE_ITEM']
  ) => void;
  @Action(StageActionsKeys.SET_STAGE) setStage!: (payload: StageActionsPayload['SET_STAGE']) => void;
  @Action(StageActionsKeys.SET_POINTER) setPointer!: (payload: StageActionsPayload['SET_POINTER']) => void;
  @Action(ElementActionsKeys.ADD_ELEMENT) addElement!: (
    payload: ElementActionsPayload['ADD_ELEMENT']
  ) => Promise<ElementActionsPayload['ADD_ELEMENT']>;
  @Action(ElementActionsKeys.UPLOAD_ELEMENT) uploadElement!: (
    payload: ElementActionsPayload['UPLOAD_ELEMENT']
  ) => Promise<ElementActionsPayload['ADD_ELEMENT']>;
  @Action(ElementActionsKeys.SELECT_ELEMENT) selectElement!: (payload: ElementActionsPayload['SELECT_ELEMENT']) => void;
  @Action(ElementActionsKeys.UNDO) undo!: (payload: ElementActionsPayload['UNDO']) => void;
  @Action(ElementActionsKeys.REDO) redo!: (payload: ElementActionsPayload['REDO']) => void;
  @Action(SessionActionsKeys.LOAD_GUEST_SESSION) loadSession!: (
    payload: SessionActionsPayload['LOAD_GUEST_SESSION']
  ) => void;

  @Getter isGuest!: RootGettersReturns['isGuest'];
  @Getter currentUser!: GuestUser;
  @Getter sessionPin!: RootGettersReturns['sessionPin'];
  @Getter sessionJoined!: RootGettersReturns['sessionJoined'];
  @Getter sessionStatus!: RootGettersReturns['sessionStatus'];
  @Getter interfaceActiveItem!: RootGettersReturns['interfaceActiveItem'];
  @Getter interfaceLaserActive!: RootGettersReturns['interfaceLaserActive'];
  @Getter sidebarWidth!: RootGettersReturns['sidebarWidth'];
  @Getter stageState!: RootGettersReturns['stageState'];
  @Getter pointerState!: RootGettersReturns['pointerState'];
  @Getter focusState!: RootGettersReturns['focusState'];
  @Getter isDoubleSelected!: RootGettersReturns['isDoubleSelected'];
  @Getter isManipulatingElement!: RootGettersReturns['isManipulatingElement'];
  @Getter selectedElement!: RootGettersReturns['selectedElement'];
  @Getter modifedStage!: RootGettersReturns['modifedStage'];
  @Getter sessionUsers!: RootGettersReturns['sessionUsers'];
  @Getter sharedBoard!: RootGettersReturns['sharedBoard'];

  laserColor(id: number) {
    const user = this.sessionUsers.find(u => u.id === id);
    return user?.colorCode || 'red';
  }

  joinSession() {
    this.sessionPaused = false;
    this.loadSession(this.currentUser.session_pin);
  }

  @Watch('stageState', {
    immediate: true,
  })
  stageChanged(newVal: Stage) {
    this.canvasPos = [newVal.x, newVal.y];
    this.canvasZoom = newVal.zoom;
    if (!this.modifedStage) {
      this.refactorStage({ ...newVal });
    } else {
      this.applyTransform();
      this.emitZoom();
    }
  }

  @Watch('focusState', {
    immediate: true,
  })
  focusChanged(newVal: Focus) {
    this.focusOnObject(newVal);
  }

  @Watch('sidebarWidth')
  updateNavOffset() {
    this.focusOnObject(this.focusState);
  }

  canvasPosDifference = [0, 0];
  canvasZoomDifference = 0;
  refactorStage({ x, y, width, height, zoom }: { [key: string]: number }) {
    this.canvasPosDifference = [0, 0];
    const newZoom = Math.min((window.innerWidth - this.sidebarWidth) / width, window.innerHeight / height) * zoom;
    this.canvasZoom = newZoom;
    const newPos = {
      x: (x / zoom) * newZoom,
      y: (y / zoom) * newZoom,
    };
    if ((window.innerWidth - this.sidebarWidth) / width > window.innerHeight / height) {
      newPos.x = newPos.x + ((window.innerWidth - this.sidebarWidth) / 2 - ((width / zoom) * newZoom) / 2);
      this.canvasPosDifference[0] = (window.innerWidth - this.sidebarWidth) / 2 - ((width / zoom) * newZoom) / 2;
    } else {
      newPos.y = newPos.y + (window.innerHeight / 2 - ((height / zoom) * newZoom) / 2);
      this.canvasPosDifference[1] = window.innerHeight / 2 - ((height / zoom) * newZoom) / 2;
    }
    this.canvasPos = [newPos.x, newPos.y];
    this.canvasZoomDifference = newZoom;
    this.applyTransform();
  }

  focusOnObject(focus: Focus) {
    if (focus) {
      let rotation = focus.rotation < 0 ? focus.rotation + 360 : focus.rotation;
      rotation = rotation * (Math.PI / 180);
      let widthWithRotation;
      let heightWithRotation;
      if (!rotation) {
        widthWithRotation = focus.width;
        heightWithRotation = focus.height;
      } else if (rotation < 90) {
        widthWithRotation = focus.width * Math.abs(Math.cos(rotation)) + focus.height * Math.abs(Math.sin(rotation));
        heightWithRotation = focus.width * Math.abs(Math.sin(rotation)) + focus.height * Math.abs(Math.cos(rotation));
      } else {
        widthWithRotation =
          focus.height * Math.abs(Math.cos(rotation - 90)) + focus.width * Math.abs(Math.sin(rotation - 90));
        heightWithRotation =
          focus.height * Math.abs(Math.sin(rotation - 90)) + focus.width * Math.abs(Math.cos(rotation - 90));
      }
      const x = focus.x * -1 + (window.innerWidth - this.sidebarWidth) / 2 - focus.width / 2;
      const y = focus.y * -1 + window.innerHeight / 2 - focus.height / 2;
      const zoom = Math.min(
        (window.innerWidth - this.sidebarWidth - 40) / widthWithRotation,
        (window.innerHeight - 40) / heightWithRotation
      );
      this.canvasZoom = 1;
      this.canvasPos = [x, y];
      this.applyTransform();
      this.zoom((window.innerWidth + this.sidebarWidth) / 2, window.innerHeight / 2, zoom, true);
    }
  }

  get pointerStateDiff() {
    return {
      ...this.calculateMouse(this.pointerState.x, this.pointerState.y),
    };
  }

  get showPendingScreen() {
    if (this.sharedBoard) return false;
    if (this.sessionStatus === SessionStatus.STARTED) {
      return false;
    } else if (this.isGuest) {
      return true;
    }
    return false;
  }

  sessionPaused = false;
  sessionDeleted = false;

  @Watch('sessionStatus')
  async pendingScreenChanged(newVal: number, oldVal: number) {
    if (typeof oldVal !== 'undefined' && oldVal !== newVal) {
      this.sessionPaused = newVal !== 4;
      this.sessionDeleted = newVal === 4;
    }
    await this.$nextTick();
    this.applyTransform();
    if (this.$refs.outerCanvas) {
      this.resizeObserver?.disconnect();
      this.resizeObserver?.observe(this.$refs.outerCanvas as HTMLElement);
    }
  }

  canvasPos = [0, 0];
  canvasZoom = 1;
  dragging = false;
  spacePressed = false;
  mousePosition: [number, number] = [0, 0];

  defaultImagePaste: {
    width: number;
    height: number;
    url: string;
    originalWidth: number;
    originalHeight: number;
    file?: null | File;
    youtube?: string;
  } = {
    width: 0,
    height: 0,
    url: '',
    originalWidth: 0,
    originalHeight: 0,
  };
  imagePaste = { ...this.defaultImagePaste };
  defaultTextPaste = {
    width: 0,
    height: 0,
    text: '',
    originalWidth: 0,
    originalHeight: 0,
  };
  textPaste = { ...this.defaultTextPaste };
  pasting = false;
  resizeObserver?: ResizeObserver;

  mounted() {
    this.sessionPaused = false;
    this.$root.$emit('checkNavBar');
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    document.addEventListener('paste', this.onPaste);
    window.addEventListener('mousemove', this.setMousePosition);
    window.addEventListener('touchmove', this.setMousePosition);
    this.resizeObserver = new ResizeObserver(this.onResize);
    if (this.$refs.outerCanvas) {
      this.resizeObserver.observe(this.$refs.outerCanvas as HTMLElement);
    }
    this.$on('updateMousePosition', this.setTransfrom);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const controlBox = (this.$refs.moveable as any)?.moveable?.innerMoveable?.moveable?.moveable?.controlBox?.element;
    if (controlBox) (controlBox as HTMLElement).style.display = 'none';
    this.applyTransform();
    if (this.interfaceLaserActive) {
      this.activateLaserPointer();
    } else {
      this.deactivateLaserPointer();
    }
    this.$nextTick(() => {
      if (this.isGuest) {
        this.$root.$emit('toggleNav', false);
      }
    });
  }

  onResize() {
    this.focusOnObject(this.focusState);
    if (!this.modifedStage) {
      this.refactorStage({ ...this.stageState });
    }
    if (!this.isGuest && !this.focusState) {
      this.setStage({ x: this.canvasPos[0], y: this.canvasPos[1], zoom: this.canvasZoom, skipEmit: this.sharedBoard });
    }
  }

  outerCanvasSize() {
    const canvas = this.$refs.outerCanvas as HTMLElement;
    if (canvas) {
      return {
        width: canvas.clientWidth,
        height: canvas.clientHeight,
      };
    } else {
      return {
        width: 500,
        height: 500,
      };
    }
  }

  beforeDestroy() {
    document.removeEventListener('paste', this.onPaste);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('mousemove', this.setMousePosition);
    window.removeEventListener('touchmove', this.setMousePosition);
    this.$off('updateMousePosition', this.setTransfrom);
    this.deactivateLaserPointer();
    this.resizeObserver?.disconnect();
  }

  private laserInterval?: NodeJS.Timeout | void;

  @Watch('interfaceLaserActive')
  laserPointerChange(newValue: boolean) {
    if (this.sharedBoard) return;
    if (this.isGuest) {
      if (newValue) {
        this.activateLaserPointer();
      } else {
        this.deactivateLaserPointer();
      }
    } else {
      if (!newValue) {
        this.deactivateLaserPointer();
      }
    }
  }

  activateLaserPointer() {
    if (this.sharedBoard) return;
    const intervalFunc = () => {
      this.setPointer({
        ...this.calculateMouseWithOffset(this.mousePosition[0] - this.sidebarWidth, this.mousePosition[1]),
        state: 0,
      });
    };
    this.laserInterval = setInterval(intervalFunc, 50);
  }

  deactivateLaserPointer() {
    if (this.sharedBoard) return;
    if (this.laserInterval) {
      this.laserInterval = clearInterval(this.laserInterval);
    }
    this.setPointer({
      ...this.calculateMouseWithOffset(this.mousePosition[0] - this.sidebarWidth, this.mousePosition[1]),
      state: 1,
    });
  }

  isTouchEvent(e: TouchEvent | MouseEvent): e is TouchEvent {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return !!(e as any).touches;
  }

  @Emit('updateMousePosition')
  setMousePosition(e: MouseEvent | TouchEvent) {
    let pos: Touch | MouseEvent;
    if (this.isTouchEvent(e)) {
      pos = e.touches[0];
    } else {
      pos = e;
    }
    this.mousePosition = [pos.clientX, pos.clientY];
    return [pos.clientX - this.sidebarWidth, pos.clientY];
  }

  transform = `translate3d(0, 0, 0) scale3d(1.0, 1.0, 1.0)`;
  transformBuffer = 0;

  async setTransfrom([x, y]: number[]) {
    this.transformBuffer++;
    if (this.transformBuffer >= 4) {
      this.transformBuffer = 0;
      this.transform = `translate3d(${x}px, ${y}px, 0) scale3d(${this.canvasZoom}, ${this.canvasZoom}, ${this.canvasZoom})`;
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.keyCode !== 32 && this.sharedBoard) return;
    if (event.keyCode === 32) {
      this.toggleSpace(true);
    } else if (this.pasting && event.keyCode === 27) {
      this.pasting = false;
      this.imagePaste = { ...this.defaultImagePaste };
      this.textPaste = { ...this.defaultTextPaste };
    } else if ((window.navigator.platform.match('Mac') ? event.metaKey : event.ctrlKey) && event.keyCode === 90) {
      if (event.shiftKey) {
        event.preventDefault();
        this.redo();
      } else {
        event.preventDefault();
        this.undo();
      }
    }
  }

  onKeyUp(event: KeyboardEvent) {
    if (event.keyCode === 32) {
      this.toggleSpace(false);
    }
  }

  toggleSpace(pressed: boolean) {
    if (this.drawing) return;
    this.spacePressed = pressed;
  }

  isActiveInterfaceItem(item: ActiveItem) {
    return this.interfaceActiveItem === item;
  }

  async clickOnCanvas(e: MouseEvent | TouchEvent) {
    const nameInput = this.$refs.nameInput as HTMLElement;
    if (nameInput) {
      nameInput.blur();
    }
    let pos;
    if (this.isTouchEvent(e)) {
      if (e.changedTouches.length) {
        const mouseX = e.changedTouches[0].clientX - this.sidebarWidth;
        const mouseY = e.changedTouches[0].clientY;

        pos = this.calculateMouseWithOffset(mouseX, mouseY);
      } else {
        pos = {
          x: 0,
          y: 0,
        };
      }
    } else {
      const mouseX = e.clientX - this.sidebarWidth;
      const mouseY = e.clientY;

      pos = this.calculateMouseWithOffset(mouseX, mouseY);
    }
    const { x, y } = pos;

    if (!this.spacePressed && !this.isManipulatingElement) {
      const allElements = Array.from(document.querySelectorAll('.Element'));
      const textElements = Array.from(document.querySelectorAll('.TextElement'));
      const clickedElement = allElements.find(el => el.contains(e.target as HTMLElement));
      if (this.sharedBoard) {
        await this.selectElement(null);
        this.$root.$emit('select', e.target);
      } else {
        if (this.pasting) {
          if (this.imagePaste.url) {
            if (this.imagePaste.file) {
              const element = await this.uploadElement({
                file: this.imagePaste.file as File,
                element: {
                  x,
                  y,
                  width: this.imagePaste.originalWidth,
                  height: this.imagePaste.originalHeight,
                  rotation: 0,
                  deleted: false,
                  data: {},
                  type: 'Image',
                },
              });
              await this.selectElement(element);
              this.$root.$emit('select', element);
            } else if (this.imagePaste.youtube) {
              const element = await this.addElement({
                type: 'Youtube',
                x,
                y,
                width: 640,
                height: 360,
                rotation: 0,
                data: {
                  id: this.imagePaste.youtube,
                },
                deleted: false,
              });
              await this.selectElement(element);
              this.$root.$emit('select', element);
            }
            this.imagePaste = { ...this.defaultImagePaste };
          } else if (this.textPaste.text) {
            const element = await this.addElement({
              type: 'Text',
              x,
              y,
              width: this.textPaste.originalWidth,
              height: this.textPaste.originalHeight,
              rotation: 0,
              data: {
                text: this.textPaste.text,
              },
              deleted: false,
            });
            this.textPaste = { ...this.defaultTextPaste };
            await this.selectElement(element);
            this.$root.$emit('select', element);
          }
          this.pasting = false;
        } else if (
          this.isActiveInterfaceItem(ActiveItem.TEXT) &&
          !textElements.some(el => el.contains(e.target as HTMLElement))
        ) {
          const element = await this.addElement({
            type: 'Text',
            x,
            y,
            width: 250,
            height: 120,
            rotation: 0,
            data: {
              text: ``,
            },
            deleted: false,
          });
          await this.selectElement(element);
          this.$root.$emit('select', element);
          this.toolbarChangeActiveItem(ActiveItem.POINTER);
        } else if (this.isActiveInterfaceItem(ActiveItem.FREE_HAND)) {
          this.drawing = false;
          await this.selectElement(null);
          this.$root.$emit('select', this.$el);
        } else if (this.isActiveInterfaceItem(ActiveItem.ERASER)) {
          this.erasing = false;
          await this.selectElement(null);
          this.$root.$emit('select', this.$el);
        } else if (this.isActiveInterfaceItem(ActiveItem.UPLOAD_BOX)) {
          const element = await this.addElement({
            type: 'UploadBox',
            x,
            y,
            width: 200,
            height: 100,
            rotation: 0,
            data: {
              shared: true,
            },
            deleted: false,
          });
          await this.selectElement(element);
          this.$root.$emit('select', element);
          this.toolbarChangeActiveItem(ActiveItem.POINTER);
        } else if (clickedElement && String(this.selectedElement?.id) === clickedElement.getAttribute('data-id')) {
          return;
        } else {
          await this.selectElement(null);
          this.$root.$emit('select', e.target);
        }
      }
    }
  }

  drawing = false;
  svgInitialPos = [0, 0];
  activeDrawingThickness = '2';
  activeDrawingColor = '#212121';
  activeAdditionalToolbar = false;

  erasing = false;

  startDrawing(e: MouseEvent | TouchEvent) {
    if (this.spacePressed || this.drawing || this.sharedBoard) return;
    if (!this.isActiveInterfaceItem(ActiveItem.FREE_HAND) && !this.isActiveInterfaceItem(ActiveItem.ERASER)) return;
    if (this.isActiveInterfaceItem(ActiveItem.FREE_HAND)) {
      this.drawing = true;
    } else {
      this.erasing = true;
    }
    this.activeAdditionalToolbar = false;
    let pos;
    if (this.isTouchEvent(e)) {
      const mouseX = e.touches[0].clientX - this.sidebarWidth;
      const mouseY = e.touches[0].clientY;

      pos = this.calculateMouseWithOffset(mouseX, mouseY);
    } else {
      const mouseX = this.mousePosition[0] - this.sidebarWidth;
      const mouseY = this.mousePosition[1];

      pos = this.calculateMouseWithOffset(mouseX, mouseY);
    }
    const { x, y } = pos;
    this.svgInitialPos = [x, y];
  }

  modifyZoom(zoomIn: boolean, setScale?: number) {
    const x = window.innerWidth / 2;
    const y = window.innerHeight / 2;
    let newScale;
    if (!setScale) {
      const zoom = zoomIn ? 2 : 0.5;
      newScale = Math.round(this.canvasZoom * zoom * 100) / 100;
    } else {
      newScale = setScale;
    }
    const scale = this.restrictScale(newScale);
    this.zoom(x, y, scale);
  }

  onMouseWheel(event: WheelEvent) {
    if (!event.altKey) return;
    event.preventDefault();
    const wheel = 0.1 * (event.deltaY < 0 ? 1 : -1);
    const newScale = this.restrictScale(this.canvasZoom * wheel + this.canvasZoom);
    this.zoom(event.clientX, event.clientY, newScale);
  }

  calculateMouseWithOffset(mouseX: number, mouseY: number) {
    const offset = {
      x: this.canvasPos[0] * -1,
      y: this.canvasPos[1] * -1,
    };

    const imageLoc = {
      x: offset.x + mouseX,
      y: offset.y + mouseY,
    };

    return {
      x: imageLoc.x / this.canvasZoom,
      y: imageLoc.y / this.canvasZoom,
    };
  }

  calculateMouse(mouseX: number, mouseY: number) {
    return {
      x: mouseX * this.canvasZoom + this.canvasPos[0],
      y: mouseY * this.canvasZoom + this.canvasPos[1],
    };
  }

  getZoom() {
    return this.canvasZoom;
  }

  zoom(mouseX: number, mouseY: number, newScale: number, skipSetStage?: boolean) {
    if (this.isGuest && !skipSetStage) return;
    mouseX -= this.sidebarWidth;

    const { x, y } = this.calculateMouseWithOffset(mouseX, mouseY);

    this.canvasZoom = newScale;

    this.canvasPos = [-(x * this.canvasZoom - mouseX), -(y * this.canvasZoom - mouseY)];

    this.applyTransform();
    this.setStage({
      x: this.canvasPos[0],
      y: this.canvasPos[1],
      zoom: this.canvasZoom,
      skipEmit: skipSetStage || this.sharedBoard,
    });
    this.emitZoom();
  }

  emitZoom() {
    if (this.sharedBoard) return;
    this.$root.$emit('zoom', this.canvasZoom);
    setTimeout(() => {
      this.$root.$emit('zoomEnd', this.canvasZoom);
    }, 300);
  }

  restrictScale(value: number) {
    return Math.min(Math.max(0.1, value), 10);
  }

  getPinchOrigin(e: TouchEvent) {
    const { clientX: x1, clientY: y1 } = e.touches[0];
    const { clientX: x2, clientY: y2 } = e.touches[1];
    const calc = (a1: number, a2: number) => {
      return (a1 + a2) / 2;
    };
    const x = calc(x1, x2);
    const y = calc(y1, y2);

    return { x, y };
  }

  async onDrop(e: DragEvent) {
    e.preventDefault();
    if (this.isGuest || this.sharedBoard) return;
    const mouseX = e.clientX - this.sidebarWidth;
    const mouseY = e.clientY;

    const mousePosition = this.calculateMouseWithOffset(mouseX, mouseY);
    onDrop({
      e,
      elPosition: mousePosition,
      addElement: this.addElement,
      uploadElement: this.uploadElement,
      afterUpload: async el => {
        await this.selectElement(el);
        this.$root.$emit('select', el);
      },
    });
  }

  async onPaste(e: ClipboardEvent) {
    const allowPasting = Array.from(document.querySelectorAll('.allow-pasting'));
    console.log(allowPasting);
    if (allowPasting.some(el => el.contains(e.target as Node))) {
      console.log('dont paste');
      return;
    }
    console.log(e);
    e.stopPropagation();
    e.preventDefault();
    if (this.isDoubleSelected && this.selectedElement?.type === 'Text') return;
    if (this.isGuest || this.sharedBoard) return;
    const clipboardData = e.clipboardData;
    const types = clipboardData?.types;
    if (!types) return;
    const calcSize = (width: number, height: number, byWidth?: boolean) => {
      const useWidth = width > height;
      let newWidth;
      let newHeight;
      if (useWidth || byWidth) {
        newWidth = Math.min(width, window.innerWidth / 3);
        newHeight = (width / newWidth) * height;
      } else {
        newHeight = Math.min(height, window.innerHeight / 3);
        newWidth = (height / newHeight) * width;
      }
      return { width: newWidth, height: newHeight };
    };
    const setImage = (
      width: number,
      height: number,
      url: string,
      originalWidth: number,
      originalHeight: number,
      file?: File | null,
      youtube?: string
    ) => {
      this.imagePaste = {
        originalWidth,
        originalHeight,
        file,
        width,
        height,
        url,
        youtube,
      };
    };

    if (types.indexOf('Files') > -1) {
      this.pasting = true;
      const items = clipboardData?.items || [];
      for (const item of items) {
        if (item.type === 'image/png') {
          const file = item.getAsFile();
          if (!file) return;
          const url = URL.createObjectURL(file);

          const image = new Image();

          image.onload = function() {
            const loadedImage = this as HTMLImageElement;
            const { width, height } = calcSize(loadedImage.width, loadedImage.height);
            setImage(width, height, url, loadedImage.width, loadedImage.height, file);
          };
          image.src = url;
        }
      }
    } else if (types.indexOf('text/plain') > -1) {
      this.pasting = true;
      const text = clipboardData?.getData('text/plain');
      if (text && isURL(text)) {
        const youtube = getIdFromUrl(text);
        if (!youtube) {
          const req = await fetch(text);
          const blob = await req.blob();
          const fileType = await getFileType(blob);
          const blobToFile = (blob: Blob, fileName: string) => {
            return new File([blob], fileName, { lastModified: new Date().getTime() });
          };
          if (fileType.type === 'image' && fileType.supported) {
            const fileName = uuid() + fileType.extension;
            const file = blobToFile(blob, fileName);
            const url = URL.createObjectURL(file);

            const image = new Image();

            image.onload = function() {
              const loadedImage = this as HTMLImageElement;
              const { width, height } = calcSize(loadedImage.width, loadedImage.height);
              setImage(width, height, url, loadedImage.width, loadedImage.height, file);
            };
            image.src = url;
          }
        } else {
          const url = `https://i.ytimg.com/vi/${youtube}/hqdefault.jpg`;
          const image = new Image();

          image.onload = function() {
            const loadedImage = this as HTMLImageElement;
            const { width, height } = calcSize(loadedImage.width, loadedImage.height);
            setImage(width, height, url, loadedImage.width, loadedImage.height, null, youtube);
          };
          image.src = url;
        }
      } else if (text) {
        const el = document.createElement('p');
        el.innerText = text;
        el.style.visibility = 'hidden';
        el.style.position = 'absolute';
        el.style.fontSize = '16px';
        el.style.fontFamily = '"Roboto", sans-serif';
        document.body.appendChild(el);
        const rect = el.getBoundingClientRect();
        const { width, height } = calcSize(rect.width, rect.height, true);
        el.style.width = `${width}px`;
        const { height: originalHeight } = el.getBoundingClientRect();
        el.remove();
        this.textPaste = {
          text,
          width,
          height,
          originalWidth: width + 4,
          originalHeight,
        };
      }
    }
  }

  onScaleStart({ set, inputEvent }: OnScaleStart) {
    if (inputEvent.touches.length !== 2) return;
    set([this.canvasZoom, this.canvasZoom]);
  }

  onScale({ inputEvent, scale }: OnScale) {
    if (inputEvent.touches.length !== 2) return;
    inputEvent.preventDefault();

    const { x, y: mouseY } = this.getPinchOrigin(inputEvent);
    const mouseX = x - this.sidebarWidth;
    const newScale = this.restrictScale(scale[0]);
    this.zoom(mouseX, mouseY, newScale);
  }

  dragBeforeStart: number[] = [];

  async onDragStart({ set }: OnDragStart) {
    set(this.canvasPos);
    this.dragBeforeStart = [...this.canvasPos];
    this.dragging = true;
    await this.selectElement(null);
    this.$root.$emit('select', this.$el);
  }

  onDrag({ beforeTranslate }: OnDrag) {
    if (
      (this.isActiveInterfaceItem(ActiveItem.TEXT) ||
        this.isActiveInterfaceItem(ActiveItem.FREE_HAND) ||
        this.isActiveInterfaceItem(ActiveItem.ERASER) ||
        this.isActiveInterfaceItem(ActiveItem.UPLOAD_BOX)) &&
      !this.spacePressed
    )
      return;
    this.canvasPos = [...beforeTranslate];
    this.applyTransform();
  }

  onDragEnd() {
    if (
      Math.abs(this.dragBeforeStart[0] - this.canvasPos[0]) > 10 ||
      Math.abs(this.dragBeforeStart[1] - this.canvasPos[1]) > 10
    ) {
      this.setStage({ x: this.canvasPos[0], y: this.canvasPos[1], zoom: this.canvasZoom, skipEmit: this.sharedBoard });
    }
    this.dragging = false;
  }

  applyTransform() {
    if (!this.$refs.innerCanvas) return;
    (this.$refs
      .innerCanvas as HTMLElement).style.transform = `translate3d(${this.canvasPos[0]}px, ${this.canvasPos[1]}px, 0) scale3d(${this.canvasZoom}, ${this.canvasZoom}, ${this.canvasZoom})`;
    this.$emit('stageUpdated', this.canvasPos, this.canvasZoom);
  }
}
